// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSpinner,
    faCheck,
    faExclamationTriangle,
    faXmark,
    faList,
    faVideo
} from '@fortawesome/free-solid-svg-icons';
import { getApiBaseUrl } from '../services/api';

interface ChannelSyncProgressProps {
    clientId: string;
    channelUrl: string;
    onClose: () => void;
    onComplete?: () => void;
}

interface SyncUpdate {
    type: 'start' | 'found' | 'progress' | 'warning' | 'error' | 'complete' | 'end';
    message: string;
    total?: number;
    current?: number;
    added?: number;
    skipped?: number;
    video?: {
        id: number;
        title: string;
        video_id: string;
    };
    error?: string;
}

const ChannelSyncProgress: React.FC<ChannelSyncProgressProps> = ({ 
    clientId, 
    channelUrl, 
    onClose,
    onComplete 
}) => {
    const [updates, setUpdates] = useState<SyncUpdate[]>([]);
    const [isComplete, setIsComplete] = useState(false);
    const [eventSource, setEventSource] = useState<EventSource | null>(null);
    const [progress, setProgress] = useState({ current: 0, total: 0, added: 0, skipped: 0 });

    useEffect(() => {
        // Connect to SSE stream
        const url = `${getApiBaseUrl()}/youtube/channel/${clientId}/sync-stream?channel_url=${encodeURIComponent(channelUrl)}`;
        const es = new EventSource(url);
        
        es.onmessage = (event) => {
            try {
                const update: SyncUpdate = JSON.parse(event.data);
                setUpdates(prev => [...prev, update]);
                
                // Update progress
                if (update.type === 'found' && update.total) {
                    setProgress(prev => ({ ...prev, total: update.total }));
                } else if (update.type === 'progress') {
                    setProgress({
                        current: update.current || 0,
                        total: update.total || 0,
                        added: update.added || 0,
                        skipped: update.skipped || 0
                    });
                } else if (update.type === 'complete') {
                    setIsComplete(true);
                    if (onComplete) {
                        onComplete();
                    }
                    // Auto close after 5 seconds
                    setTimeout(() => {
                        es.close();
                        onClose();
                    }, 5000);
                } else if (update.type === 'end') {
                    es.close();
                }
            } catch (error) {
                console.error('Error parsing SSE data:', error);
            }
        };
        
        es.onerror = () => {
            console.error('SSE connection error');
            es.close();
            setIsComplete(true);
        };
        
        setEventSource(es);
        
        // Cleanup
        return () => {
            if (es) {
                es.close();
            }
        };
    }, [clientId, channelUrl, onClose, onComplete]);

    const progressPercent = progress.total > 0 
        ? Math.round((progress.current / progress.total) * 100) 
        : 0;

    const hasErrors = updates.some(u => u.type === 'error' || u.type === 'warning');

    return (
        <div className="fixed bottom-4 right-4 z-50 w-96">
            <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className={`flex items-center justify-between p-3 ${
                    isComplete ? (hasErrors ? 'bg-yellow-50' : 'bg-brand-50') : 'bg-brand-50'
                }`}>
                    <div className="flex items-center gap-2">
                        {isComplete ? (
                            <FontAwesomeIcon icon={faCheck} className="text-brand-600" />
                        ) : (
                            <FontAwesomeIcon icon={faSpinner} className="text-brand-500 animate-spin" />
                        )}
                        <span className="font-medium text-sm text-gray-800">
                            {isComplete ? 'Channel Sync Complete' : 'Syncing YouTube Channel'}
                        </span>
                    </div>
                    <button
                        onClick={() => {
                            if (eventSource) {
                                eventSource.close();
                            }
                            onClose();
                        }}
                        className="text-gray-500 hover:text-gray-700 p-1"
                    >
                        <FontAwesomeIcon icon={faXmark} className="text-sm" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4">
                    {/* Progress Bar */}
                    {progress.total > 0 && (
                        <div className="mb-3">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                                <span>{progress.current} of {progress.total} videos</span>
                                <span>{progressPercent}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                    className={`h-2 rounded-full transition-all duration-500 ${
                                        isComplete ? 'bg-brand-500' : 'bg-brand-500'
                                    }`}
                                    style={{ width: `${progressPercent}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Stats */}
                    {(progress.added > 0 || progress.skipped > 0) && (
                        <div className="flex items-center justify-between text-xs mb-3">
                            <div className="flex items-center gap-1 text-brand-600">
                                <FontAwesomeIcon icon={faVideo} />
                                <span>{progress.added} added</span>
                            </div>
                            
                            {progress.skipped > 0 && (
                                <div className="flex items-center gap-1 text-gray-500">
                                    <FontAwesomeIcon icon={faList} />
                                    <span>{progress.skipped} already exist</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Recent Updates */}
                    <div className="max-h-40 overflow-y-auto space-y-1">
                        {updates.slice(-5).map((update, index) => (
                            <div key={index} className={`text-xs ${
                                update.type === 'error' ? 'text-red-600' :
                                update.type === 'warning' ? 'text-yellow-600' :
                                update.type === 'complete' ? 'text-brand-600 font-medium' :
                                'text-gray-600'
                            }`}>
                                {update.message}
                            </div>
                        ))}
                    </div>

                    {/* Completion Message */}
                    {isComplete && !hasErrors && (
                        <div className="mt-3 p-2 bg-brand-50 rounded text-xs text-brand-700 font-medium">
                            Successfully synced {progress.added} new videos from the channel!
                        </div>
                    )}

                    {isComplete && hasErrors && (
                        <div className="mt-3 p-2 bg-yellow-50 rounded text-xs text-yellow-700 font-medium">
                            Sync completed with some errors. Check the videos list for details.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChannelSyncProgress;